import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, User, AlertTriangle, UserPlus, X, Key, Pencil, Trash2 } from 'lucide-react';
import { api } from '../../api/client';
import { useCompany } from '../../contexts/CompanyContext';

export default function Users() {
  const navigate = useNavigate();
  const { activeCompanyId } = useCompany();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [editRole, setEditRole] = useState('sub_user');
  const [savingRole, setSavingRole] = useState(false);
  const [editError, setEditError] = useState(null);
  const [deletingMember, setDeletingMember] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    fetchMembers();
  }, [activeCompanyId]);

  const fetchMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/company/members');
      setMembers(res.data);
    } catch (err) {
      console.error('Failed to fetch members:', err);
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const openEditModal = (member) => {
    setEditingMember(member);
    setEditRole(member.role);
    setEditError(null);
  };

  const closeEditModal = () => {
    setEditingMember(null);
    setEditError(null);
  };

  const saveAccessType = async () => {
    if (!editingMember || editRole === editingMember.role) {
      closeEditModal();
      return;
    }

    setSavingRole(true);
    setEditError(null);
    try {
      const res = await api.patch(`/api/company/members/${editingMember.id}/role`, { role: editRole });
      setMembers((prev) =>
        prev.map((m) =>
          m.id === editingMember.id ? { ...m, role: res.data.role, permissions: res.data.permissions } : m
        )
      );
      closeEditModal();
    } catch (err) {
      console.error('Failed to update access type:', err);
      setEditError(err.response?.data?.detail || 'Failed to update access type');
    } finally {
      setSavingRole(false);
    }
  };

  const openDeleteModal = (member) => {
    setDeletingMember(member);
    setDeleteError(null);
  };

  const closeDeleteModal = () => {
    setDeletingMember(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deletingMember) return;

    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/api/company/members/${deletingMember.id}`);
      setMembers((prev) => prev.filter((m) => m.id !== deletingMember.id));
      closeDeleteModal();
    } catch (err) {
      console.error('Failed to remove member:', err);
      setDeleteError(err.response?.data?.detail || 'Failed to remove user');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Users</h1>
          <p className="text-gray-600 mt-1">
            People with access to your company account
          </p>
        </div>
        <Link
          to="/dashboard/invite-users"
          className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded hover:bg-blue-700 flex items-center justify-center gap-2 text-sm sm:text-base"
        >
          <UserPlus className="w-4 h-4" />
          Invite Users
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : members.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-gray-300" />
            No users yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="px-6 py-3 font-medium">Name</th>
                  <th className="px-6 py-3 font-medium">Email</th>
                  <th className="px-6 py-3 font-medium">Role</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Joined</th>
                  <th className="px-6 py-3 font-medium text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.id} className="border-b last:border-b-0">
                    <td className="px-6 py-3 text-gray-800">{member.full_name || '-'}</td>
                    <td className="px-6 py-3 text-gray-600">{member.email}</td>
                    <td className="px-6 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          member.role === 'owner' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {member.role === 'owner' ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                        {member.role === 'owner' ? 'Full Access' : 'Limited Access'}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          member.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {member.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-gray-600">{formatDate(member.joined_at)}</td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {member.role === 'sub_user' && (
                          <button
                            onClick={() => navigate(`/dashboard/users/${member.id}/privileges`, { state: { member } })}
                            className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
                            title="Manage privileges"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => openEditModal(member)}
                          className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                          title="Edit user"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(member)}
                          className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-800">Edit Access Type</h2>
              <button onClick={closeEditModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-gray-600">
                {editingMember.full_name || editingMember.email}
              </p>

              {editError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                  {editError}
                </div>
              )}

              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="accessType"
                  value="owner"
                  checked={editRole === 'owner'}
                  onChange={() => setEditRole('owner')}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-800">Full Access</span>
                  <span className="block text-xs text-gray-500">Unrestricted access to the company account</span>
                </span>
              </label>

              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="accessType"
                  value="sub_user"
                  checked={editRole === 'sub_user'}
                  onChange={() => setEditRole('sub_user')}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-800">Limited Access</span>
                  <span className="block text-xs text-gray-500">Access restricted to assigned menu privileges</span>
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-2 px-5 py-4 border-t">
              <button
                onClick={closeEditModal}
                disabled={savingRole}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={saveAccessType}
                disabled={savingRole}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {savingRole ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deletingMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-800">Remove User</h2>
              <button onClick={closeDeleteModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-gray-600">
                Are you sure you want to remove{' '}
                <span className="font-medium text-gray-800">
                  {deletingMember.full_name || deletingMember.email}
                </span>
                ? They will lose access to your company account.
              </p>

              {deleteError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                  {deleteError}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 px-5 py-4 border-t">
              <button
                onClick={closeDeleteModal}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                No
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Removing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
